import { useId } from 'react'
import { cn } from '@/lib/utils'

interface SparklineProps {
  data: number[]
  /** Tailwind text color class; line + area derive from currentColor */
  colorClass?: string
  height?: number
  className?: string
}

/**
 * Minimal line-with-area sparkline. No axes, no labels.
 * Renders nothing for fewer than 2 points.
 */
export function Sparkline({
  data,
  colorClass = 'text-primary-600',
  height = 28,
  className,
}: SparklineProps) {
  const gradientId = useId()
  if (data.length < 2) return null

  const width = 100
  const inset = 2
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min

  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width
    // Flat data sits at mid-height
    const y =
      range === 0 ? height / 2 : inset + (1 - (value - min) / range) * (height - inset * 2)
    return [x, y] as const
  })

  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ')
  const area = `${line} L${width},${height} L0,${height} Z`

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn('w-full', colorClass, className)}
      style={{ height }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.12" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
